import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { Check, Pencil, Plus, Star } from 'lucide-react';

import { createProductReview, loadProductReviews, updateProductReview } from './api';
import {
  Empty, Failed, Loading, Pill,
} from './parts';
import { Field, errorMessage, fieldAria } from './ui';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import { Switch } from './ProductOptions';
import './reviews.css';

// The Reviews tab of a product's sheet: what customers say about the product,
// as its page on the storefront shows it. Only published reviews are on the
// page, and the product's stars are their average; a review is taken off the
// page by unpublishing it, never deleted.

const STARS = [1, 2, 3, 4, 5];

function Stars({ value }) {
  return <span className="xo-review-stars" role="img" aria-label={tx('ui.products.reviews.stars_of', { count: value })}>
    {STARS.map((star) => <Star key={star} aria-hidden="true" className={star <= value ? 'is-on' : ''} />)}
  </span>;
}

function formOf(review) {
  if (!review) {
    return {
      reviewerName: '', rating: 5, body: '', sortOrder: '0', isPublished: true,
    };
  }
  return {
    reviewerName: review.reviewerName,
    rating: review.rating,
    body: review.body,
    sortOrder: String(review.sortOrder),
    isPublished: review.isPublished,
  };
}

// The review as XOmics takes it (ProductReviews.js readReview). Throws the
// message the form shows.
function bodyOf(form, review, productId) {
  const sortOrder = form.sortOrder.trim() === '' ? 0 : Number(form.sortOrder);
  const details = {
    ...(review ? {} : { productId }),
    reviewerName: form.reviewerName.trim(),
    rating: form.rating,
    body: form.body.trim(),
    sortOrder,
    isPublished: form.isPublished,
  };
  if (!details.reviewerName || !details.body || !Number.isSafeInteger(sortOrder)) {
    throw new Error(tx('ui.products.reviews.complete_fields'));
  }
  return details;
}

function ReviewForm({
  review, productId, onCancel, onSaved, onDirtyChange,
}) {
  const initial = useMemo(() => formOf(review), [review]);
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  const id = review ? `review-${review.id}` : 'review-new';
  const set = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  useEffect(() => {
    onDirtyChange(dirty);
    return () => onDirtyChange(false);
  }, [dirty, onDirtyChange]);

  async function submit(event) {
    event.preventDefault();
    setError('');
    let details;
    try {
      details = bodyOf(form, review, productId);
    } catch (invalid) {
      setError(invalid.message);
      return;
    }
    setBusy(true);
    try {
      if (review) await updateProductReview(review.id, details);
      else await createProductReview(details);
      onSaved(review ? tx('ui.products.reviews.updated') : tx('ui.products.reviews.created'));
    } catch (requestError) {
      setError(errorMessage(requestError));
      setBusy(false);
    }
  }

  return <form className="xo-pm-card xo-review-form" onSubmit={submit} aria-labelledby={`${id}-title`}>
    <h3 id={`${id}-title`}>{review ? tx('ui.products.reviews.edit_title') : tx('ui.products.reviews.new_title')}</h3>
    {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
    <div className="xo-product-details-grid">
      <Field id={`${id}-name`} label={tx('ui.products.reviews.name')}>
        <input {...fieldAria(`${id}-name`)} type="text" value={form.reviewerName} maxLength="120" disabled={busy}
          placeholder={tx('ui.products.reviews.name_placeholder')}
          onChange={(event) => set('reviewerName', event.target.value)} />
      </Field>
      <div className="xomics-field">
        <span className="xo-review-label">{tx('ui.products.reviews.stars')}</span>
        <div className="xo-review-pick" role="radiogroup" aria-label={tx('ui.products.reviews.stars')}>
          {STARS.map((star) => <button type="button" role="radio" key={star}
            aria-checked={form.rating === star} aria-label={tx('ui.products.reviews.stars_of', { count: star })}
            className={star <= form.rating ? 'is-on' : ''} disabled={busy}
            onClick={() => set('rating', star)}><Star aria-hidden="true" /></button>)}
          <b>{form.rating}</b>
        </div>
      </div>
      <div className="xo-product-field-wide">
        <Field id={`${id}-body`} label={tx('ui.products.reviews.words')}>
          <textarea {...fieldAria(`${id}-body`)} rows="5" dir="auto" value={form.body} maxLength="4000"
            disabled={busy} onChange={(event) => set('body', event.target.value)} />
        </Field>
      </div>
      <Field id={`${id}-order`} label={tx('ui.products.reviews.position')} hint={tx('ui.products.reviews.position_hint')}>
        <input {...fieldAria(`${id}-order`, { hint: true })} type="number" step="1" value={form.sortOrder}
          disabled={busy} onChange={(event) => set('sortOrder', event.target.value)} />
      </Field>
    </div>
    <div className="xo-pm-page-switch">
      <span>
        <strong>{tx('ui.products.reviews.published')}</strong>
        <small>{form.isPublished
          ? tx('ui.products.reviews.on_the_page') : tx('ui.products.reviews.not_on_the_page')}</small>
      </span>
      <Switch on={form.isPublished} disabled={busy} label={tx('ui.products.reviews.published')}
        onChange={(value) => set('isPublished', value)} />
    </div>
    <p className="xo-pm-hint">{tx('ui.products.reviews.no_delete')}</p>
    <div className="xo-review-form-actions">
      <button type="button" className="xo-btn-2" disabled={busy} onClick={onCancel}>
        {tx('ui.products.coupons.cancel')}</button>
      <button type="submit" className="xo-btn" disabled={busy}>
        {busy ? tx('ui.products.coupons.saving') : tx('ui.products.coupons.save')}</button>
    </div>
  </form>;
}

// `onCount` tells the sheet how many reviews the product has (its tab shows
// the number); `onDirtyChange` whether a review is being written, so the sheet
// asks before it closes over it.
export default function ProductReviews({
  product, mayManage, onCount, onDirtyChange,
}) {
  const list = useLiveResource(['products'], useCallback(() => loadProductReviews(product.id), [product.id]));
  // undefined: no form; null: a new review; an id: that review.
  const [openId, setOpenId] = useState(undefined);
  const [notice, setNotice] = useState('');
  const reviews = useMemo(() => list.data ?? [], [list.data]);
  // The stars the product's page shows: the average of its published reviews.
  const published = reviews.filter((review) => review.isPublished);
  const average = published.length === 0 ? null
    : Math.round((published.reduce((sum, review) => sum + review.rating, 0) / published.length) * 10) / 10;

  useEffect(() => { if (list.data !== null) onCount(list.data.length); }, [list.data, onCount]);

  if (list.loading && list.data === null) return <Loading label={tx('ui.products.reviews.loading')} />;
  if (list.error) return <Failed message={list.error} onRetry={list.refresh} />;

  const saved = (message) => { setOpenId(undefined); setNotice(message); list.refresh(); };
  const form = (review) => <ReviewForm key={review?.id ?? 'new'} review={review} productId={product.id}
    onCancel={() => setOpenId(undefined)} onSaved={saved} onDirtyChange={onDirtyChange} />;

  return <div className="xo-review-tab">
    <div className="xo-pm-section-head">
      <div>
        <h3>{tx('ui.products.reviews.tab')}</h3>
        <p>{tx('ui.products.reviews.tab_detail')}</p>
      </div>
      {mayManage && openId === undefined && (
        <button type="button" className="xo-btn" onClick={() => { setNotice(''); setOpenId(null); }}>
          <Plus aria-hidden="true" />{tx('ui.products.reviews.new')}</button>
      )}
    </div>
    {notice && <p className="xo-alert t-ok" role="status"><Check aria-hidden="true" />{notice}</p>}
    {reviews.length > 0 && <p className="xo-review-average">
      {average === null ? tx('ui.products.reviews.no_stars')
        : <><Stars value={Math.round(average)} /><b>{average.toFixed(1)}</b>
          {tx('ui.products.reviews.from_published', { count: published.length })}</>}
    </p>}
    {openId === null && form(null)}
    {reviews.length === 0 && openId === undefined && <Empty title={tx('ui.products.reviews.empty')}
      detail={tx('ui.products.reviews.empty_detail')} />}
    {reviews.length > 0 && <ul className="xo-review-list">
      {reviews.map((review) => (openId === review.id
        ? <li key={review.id}>{form(review)}</li>
        : <li key={review.id} className={`xo-review-row${review.isPublished ? '' : ' is-off'}`}>
          <div className="xo-review-row-head">
            <Stars value={review.rating} />
            <Pill tone={review.isPublished ? 'ok' : 'flat'}>{review.isPublished
              ? tx('ui.products.reviews.published') : tx('ui.products.reviews.hidden')}</Pill>
            {mayManage && openId === undefined && (
              <button type="button" className="xo-btn-2 xo-review-edit"
                aria-label={tx('ui.products.reviews.edit_of', { name: review.reviewerName })}
                onClick={() => { setNotice(''); setOpenId(review.id); }}>
                <Pencil aria-hidden="true" />{tx('ui.products.reviews.edit')}</button>
            )}
          </div>
          <p className="xo-review-words" dir="auto">{review.body}</p>
          <p className="xo-review-by">{review.reviewerName}</p>
        </li>))}
    </ul>}
  </div>;
}
